import { LogDebug } from './Logging';
import {
  getFirstOrDefaultLoggedInHost,
  getLoggedInUrlFromPublicUrl,
  getPublicUrlFromLoggedInUrl,
  getRedirectPodUrlByRole,
  getRouteByTier,
} from './RedirectRouteMapper';
import { USER_AUX_INFO_COOKIES } from '../constants/Constants';
import { IEmServiceDetails, IEmAuthCookies, ILoggerDetails } from '../types/types';
import { tryBackgroundRefreshToken, triggerClientSideAccessTokenRefresh } from './BackgroundRefreshToken';
import { constructUrl } from '../utils/urlUtils';
import {
  isPublicService,
  isLoggedInOnlyService,
  supportsPublicUsers,
  supportsFreemiumUsers,
  hasLoggedInAlternativeService,
  hasPublicAlternativeService,
  supportsProUsers,
  getEmServiceErrors,
  getServiceTierByUser,
} from './TierHelper';

/**
 * Parameters for validateServiceAccess function
 */
export interface ServiceAccessParams {
  originalUrl: string;
  emService: IEmServiceDetails;
  emAuthCookies: IEmAuthCookies;
  emReAuthenticationServiceBaseUrl: string;
  isClientSide?: boolean;
  logger?: ILoggerDetails;
}

/**
 * Determines the appropriate redirect URL and cookies to clear based on the user's authentication status and the requested URL.
 *
 * Main decision branches:
 * 1. Public service:
 *    - Logged in: redirect to PRO/FREEMIUM equivalent if available
 *    - em_at expired but em_rte valid: redirect to login with returnUrl to PRO/FREEMIUM
 *    - Logged out: stay on public, clear cookies
 * 2. PRO/FREEMIUM service:
 *    - Logged in: if cross-tier (FREEMIUM on PRO EA or PRO on FREEMIUM EA), redirect to alternate; else stay. This is only for EA services, otherwise don't redirect.
 *    - em_at expired but em_rte valid: redirect to login with returnUrl to current page
 *    - Logged out: redirect to public equivalent if available, else to login with returnUrl to current page; clear cookies if redirecting to public
 *
 * @param {Object} params - Object containing all parameters needed for service access validation
 * @param {string} params.originalUrl - The original URL requested by the user
 * @param {IEmServiceDetails} params.emService - Service details including tier and alternative service availability
 * @param {IEmAuthCookies} params.emAuthCookies - User's authentication cookies (em_at, em_rte)
 * @param {string} params.emReAuthenticationServiceBaseUrl - URL for the authentication service
 * @param {boolean} params.isClientSide - Whether this is being called from client-side code
 * @param {ILoggerDetails} [params.logger] - Optional logger for debug information
 * @returns {{ redirectUrl: string | null; emCookiesToTrash: string[] }} An object containing the redirect URL and an array of cookies to clear.
 */
export async function validateServiceAccess({
  originalUrl,
  emService,
  emAuthCookies,
  emReAuthenticationServiceBaseUrl,
  isClientSide = false,
  logger,
}: ServiceAccessParams): Promise<{
  redirectUrl: string | null;
  emCookiesToTrash: string[];
}> {
  let redirectUrl: string | null = null;
  const emCookiesToTrash: string[] = [];

  // This helper is used to redirect users to the login page when their session needs to be refreshed or re-authenticated.
  function buildLoginRedirect(returnUrl: string) {
    return constructUrl(emReAuthenticationServiceBaseUrl, `/Account/Login?returnUrl=${encodeURIComponent(returnUrl)}`);
  }

  LogDebug(
    logger,
    `validateServiceAccess() called with ${originalUrl}; emService: ${JSON.stringify(
      emService
    )};  emAuthCookies: ${JSON.stringify(
      emAuthCookies
    )}, emReAuthenticationServiceBaseUrl: ${emReAuthenticationServiceBaseUrl}; isClientSide: ${isClientSide} `
  );

  const emServiceModelValidationErrors = getEmServiceErrors(emService, logger);
  if (emServiceModelValidationErrors) {
    throw new Error(`emService validation failed with error: ${emServiceModelValidationErrors}`);
  }

  // EDGE CASE #1: service supports PUBLIC+FREEMIUM only (we don't have one ATM)
  // EDGE CASE #2: service supports PUBLIC+PRO only (we don't have one ATM)
  // EDGE CASE #3: service supports PRO only and doesn't have PUBLIC equivalent (not sure we have one now... but maybe, someday)

  // Handle logged-in user on public service -> redirect to PRO service
  if (isPublicService(emService, logger) && emAuthCookies.em_at) {
    LogDebug(logger, `validateServiceAccess() :: on public service and em_at exists`);

    if (hasLoggedInAlternativeService(emService)) {
      const userTier = getServiceTierByUser(emAuthCookies.em_at);
      const newHost = getRouteByTier(emService, userTier);
      redirectUrl = newHost ? getLoggedInUrlFromPublicUrl(originalUrl, newHost, logger) : null;
      LogDebug(logger, `validateServiceAccess() :: ori: ${originalUrl}, redirect: ${redirectUrl}`);
    }

    if (!redirectUrl) {
      LogDebug(logger, `validateServiceAccess() :: no loggedin -> staying put on public service ${originalUrl}`);
    }
  }
  // Handle logged-in user on public service (but temp access token needs to be refreshed) -> redirect to re-authentication service -> then to Freemium page
  else if (isPublicService(emService, logger) && !emAuthCookies.em_at && emAuthCookies.em_rte) {
    LogDebug(logger, `validateServiceAccess() :: on public service and em_at doesn't exist, but em_rte exists`);

    let loggedInUrl;
    if (hasLoggedInAlternativeService(emService)) {
      const newHost = getFirstOrDefaultLoggedInHost(emService);
      loggedInUrl = newHost ? getLoggedInUrlFromPublicUrl(originalUrl, newHost, logger) : null;
    }

    // even though user might have to stay on current service, if their em_at expired --> refresh it now
    redirectUrl = loggedInUrl ? buildLoginRedirect(loggedInUrl) : buildLoginRedirect(originalUrl);

    LogDebug(logger, `validateServiceAccess() :: redirecting from ${originalUrl} to ${redirectUrl}`);
  }
  // Handle logged-out user on public service -> stay on public service (but clean up potential lingering auth cookies)
  else if (isPublicService(emService, logger) && !emAuthCookies.em_at && !emAuthCookies.em_rte) {
    LogDebug(logger, `validateServiceAccess() :: on public service and em_at/em_rte don't exist`);
    LogDebug(
      logger,
      `validateServiceAccess() :: staying put && deleting cookies [${JSON.stringify(USER_AUX_INFO_COOKIES)}]`
    );

    emCookiesToTrash.push(...USER_AUX_INFO_COOKIES);
  }
  // Handle logged-in user(PRO or FREEMIUM) on PRO/FREEMIUM service
  else if (isLoggedInOnlyService(emService, logger) && emAuthCookies.em_at) {
    LogDebug(logger, `validateServiceAccess() :: on logged-in service and em_at exists`);

    if (hasLoggedInAlternativeService(emService)) {
      // will set to new URL if redirect is needed, else will set to null (meaning no need to redirect)
      redirectUrl = getRedirectPodUrlByRole({ originalUrl, emAuthCookies, emService, logger });
    }

    if (redirectUrl) {
      LogDebug(
        logger,
        `em_at valid for user -> should be redirecting from ${originalUrl} to alternate service ${redirectUrl}`
      );
    } else {
      LogDebug(logger, `em_at valid for user -> staying put on ${originalUrl}`);

      // Check if auth token is close to being expired, within 25 minutes. Just refresh token client side in the background to avoid authentication service url redirect
      if (isClientSide) {
        LogDebug(logger, `Running client side -> checking if token needs to be refreshed`);
        tryBackgroundRefreshToken(emReAuthenticationServiceBaseUrl, logger);
      }
    }
  }
  // logged-in user on PRO/FREEMIUM service (but temp access token needs to be refreshed) -> refresh token () -> else redirect to re-authentication service -> then back to current PRO page
  else if (isLoggedInOnlyService(emService, logger) && !emAuthCookies.em_at && emAuthCookies.em_rte) {
    LogDebug(logger, `validateServiceAccess() :: on logged-in service and em_at desn't exist, but em_rte exists`);

    // presuming we'd have to redirect to "login" page to refresh the "em_at" and come back to current page
    let isSkipReAuthentication = false;

    // if the user is client side, we may not need to redirect, but can silently refresh access token client-side
    if (isClientSide) {
      LogDebug(logger, `Running client side -> will try silently refreshing em_at client-side`);
      try {
        const isRefreshSuccess = await triggerClientSideAccessTokenRefresh(emReAuthenticationServiceBaseUrl, logger);
        if (isRefreshSuccess) {
          isSkipReAuthentication = true; // !important: if client-side token refresh succeeded, then no need to redirect to login page to then come back to current page again
        }
      } catch (error) {
        LogDebug(logger, `Token refresh promise rejected, will redirect to login`);
      }
    }

    if (!isSkipReAuthentication) {
      redirectUrl = buildLoginRedirect(originalUrl);
    }

    LogDebug(
      logger,
      `Accessing ${JSON.stringify(
        emService
      )} -> User's [em_at] expired, but session [em_rte] is still valid -> redirecting from ${originalUrl} to ${redirectUrl}`
    );
  }
  // Handle logged-out user on PRO or FREEMIUM service -> redirect to matching public service OR (otherwise) to login page (with optional return to this PRO page after signing-in)
  else if (isLoggedInOnlyService(emService, logger) && !emAuthCookies.em_at && !emAuthCookies.em_rte) {
    LogDebug(logger, `validateServiceAccess() :: on logged-in service and em_at/em_rte don't exist`);

    //
    if (hasPublicAlternativeService(emService)) {
      redirectUrl = getPublicUrlFromLoggedInUrl({
        loggedInUrl: originalUrl,
        emService,
        logger,
      });
    }

    if (!redirectUrl) {
      redirectUrl = buildLoginRedirect(originalUrl);
    }
    emCookiesToTrash.push(...USER_AUX_INFO_COOKIES);

    LogDebug(
      logger,
      `User is not logged in (trying to access PRO/FREEMIUM service) -> will be redirecting from [${originalUrl}] to [${redirectUrl}]`
    );
  }
  // "everything-bagel" service - supports PUBLIC/FREEMIUM/PRO (e.g. soon-to-be Public.Content.Articles, once Subscription.Content.Articles is decommissioned) or PremiumForecasts -> just let the user stay where they are (+refresh em_at if needed) and allow the service to handle the experience
  else if (
    supportsPublicUsers(emService, logger) &&
    supportsFreemiumUsers(emService, logger) &&
    supportsProUsers(emService, logger)
  ) {
    LogDebug(logger, `validateServiceAccess() :: on everything-bagel service`);

    // presuming we'd have to redirect to "login" page to refresh the "em_at" and come back to current page
    if (!emAuthCookies.em_at && emAuthCookies.em_rte) {
      let isEmAtRenewNeeded = true;
      // if the user is client side, we may not need to redirect, but can silently refresh access token client-side
      if (isClientSide) {
        if (isEmAtRenewNeeded) {
          LogDebug(logger, `Running client side -> will try silently refreshing em_at client-side`);
          try {
            const isRefreshSuccess = await triggerClientSideAccessTokenRefresh(
              emReAuthenticationServiceBaseUrl,
              logger
            );
            if (isRefreshSuccess) {
              isEmAtRenewNeeded = false; // !important: if client-side token refresh succeeded, then no need to redirect to login page to then come back to current page again
            }
          } catch (error) {
            LogDebug(logger, `Token refresh promise rejected, will redirect to login`);
          }
        } else {
          LogDebug(logger, `Running client side -> checking if token needs to be refreshed`);
          tryBackgroundRefreshToken(emReAuthenticationServiceBaseUrl, logger);
        }
      }
      if (isEmAtRenewNeeded) {
        redirectUrl = buildLoginRedirect(originalUrl);
      }
    } else if (!emAuthCookies.em_rte) {
      emCookiesToTrash.push(...USER_AUX_INFO_COOKIES);
    } else {
      // stay on this service as "anon user"
    }

    LogDebug(
      logger,
      `Accessing ${JSON.stringify(
        emService
      )} -> User's [em_at] expired, but session [em_rte] is still valid -> redirecting from ${originalUrl} to ${redirectUrl}`
    );
  }

  return {
    redirectUrl,
    emCookiesToTrash,
  };
}
